/**
 * Demo Scheduling Configuration - 100% FREE SOLUTION
 *
 * Central configuration for all demo scheduling functionality
 * Uses Google Calendar appointment slots or simple contact forms - NO PAID SERVICES
 */

export const SCHEDULING_CONFIG = {
  // FREE Google Calendar Approach
  googleCalendar: {
    // Your actual Google Calendar appointment scheduling URL
    appointmentSlotsUrl:
      'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1LCP3XRx7RBNvrOvV9RQdYWU5OEzKRS5tkgmf4vU46PSnbPJMPjAj74Mh70b2V30ev-mcT6wq0?gv=true',

    // Instructions for setup:
    // 1. Go to Google Calendar
    // 2. Click gear icon → Settings → Appointment schedules
    // 3. Create new appointment schedule:
    //    - Name: "Community App Demo"
    //    - Duration: 30 minutes
    //    - Form: Ask for Name, Email, Community Name
    //    - Include question: "What email did you use for your community assessment?"
    // 4. Copy the booking page URL and replace YOUR_SCHEDULE_ID above

    // Demo session settings
    defaultDuration: 30, // minutes
    workingHours: {
      start: '09:00',
      end: '17:00',
      timezone: 'America/Denver', // Mountain Time for Montana
    },
  },

  // Assessment requirement configuration
  assessmentRequired: {
    // Must complete assessment before scheduling
    enforceAssessmentComplete: true,

    // Redirect paths for incomplete assessments
    redirects: {
      headerCTA: '/onboarding/welcome/silver', // Header "Get Started"
      pricingPageSilver: '/pricing/assessment?tier=silver',
      pricingPageGold: '/pricing/assessment?tier=gold',
      pricingPageCustom: '/onboarding/welcome/contact-sales',
    },
  },

  // Demo session configuration
  demo: {
    duration: 30, // minutes
    features: [
      'Board governance & voting tools',
      'Financial management & reporting',
      'Resident communication features',
      'Facility booking system',
      'Document management',
      'Custom pricing based on your assessment',
    ],

    // What we need to know before demo
    requiredAssessmentData: [
      'communityName',
      'totalUnits',
      'contactName',
      'contactEmail',
      'primaryGoals',
      'currentChallenges',
    ],
  },

  // Analytics event names
  analytics: {
    events: {
      ASSESSMENT_STARTED: 'assessment_started',
      ASSESSMENT_COMPLETED: 'assessment_completed',
      DEMO_ACCESSED: 'demo_scheduling_accessed',
      DEMO_SCHEDULED: 'demo_scheduled',
      BLOCKED_NO_ASSESSMENT: 'demo_blocked_no_assessment',
    },
  },
};

/**
 * FREE Scheduling Utility Functions
 */
export const schedulingUtils = {
  /**
   * Check if user has completed required assessment
   */
  hasCompletedAssessment(): boolean {
    // Check localStorage for completed assessment
    const onboardingProgress = localStorage.getItem('assessment_progress');
    const pricingProgress = localStorage.getItem('pricing_assessment_progress');

    if (onboardingProgress) {
      const data = JSON.parse(onboardingProgress);
      return data.completed === true;
    }

    if (pricingProgress) {
      const data = JSON.parse(pricingProgress);
      return data.completed === true;
    }

    return false;
  },

  /**
   * Get assessment completion status and redirect URL
   */
  getAssessmentStatus(source: 'header' | 'pricing-silver' | 'pricing-gold' | 'pricing-custom') {
    const hasCompleted = this.hasCompletedAssessment();

    if (hasCompleted) {
      return {
        canSchedule: true,
        redirectUrl: null,
        message: 'Assessment completed - ready to schedule!',
      };
    }

    const redirectMap = {
      header: SCHEDULING_CONFIG.assessmentRequired.redirects.headerCTA,
      'pricing-silver': SCHEDULING_CONFIG.assessmentRequired.redirects.pricingPageSilver,
      'pricing-gold': SCHEDULING_CONFIG.assessmentRequired.redirects.pricingPageGold,
      'pricing-custom': SCHEDULING_CONFIG.assessmentRequired.redirects.pricingPageCustom,
    };

    return {
      canSchedule: false,
      redirectUrl: redirectMap[source],
      message:
        'Please complete the community assessment first to help us prepare a personalized demo.',
    };
  },

  /**
   * Get completed assessment data from localStorage
   */
  getCompletedAssessmentData(): Record<string, unknown> | null {
    const onboardingProgress = localStorage.getItem('assessment_progress');
    const pricingProgress = localStorage.getItem('pricing_assessment_progress');

    if (onboardingProgress) {
      return JSON.parse(onboardingProgress).data;
    }

    if (pricingProgress) {
      return JSON.parse(pricingProgress).data;
    }

    return null;
  },

  /**
   * Track scheduling analytics
   */
  trackEvent(eventName: string, additionalData: Record<string, unknown> = {}): void {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (
        window as typeof window & {
          gtag: (command: string, eventName: string, parameters?: Record<string, unknown>) => void;
        }
      ).gtag;

      gtag('event', eventName, {
        event_category: 'scheduling',
        event_label: 'free_scheduling_system',
        ...additionalData,
      });
    }
  },
};

export default SCHEDULING_CONFIG;
